import { put, call, select, all, take, race, SagaReturnType } from 'redux-saga/effects'
import moment from 'moment'

// Redux Actions
import HabitActions from 'APP/Redux/HabitsRedux'
import { PatientTypes } from 'APP/Redux/PatientRedux'
import { featuresActions } from 'APP/Redux/FeaturesSlice'
import { LoginTypes } from 'APP/Redux/LoginRedux'

// Services
import Config from 'APP/Config'
import { Habits } from '@dialogue/services'
import { getHabit } from '../Services/CMS'
import {
  removeLocalNotificationsForOneHabit,
  updateLocalNotificationsForOneHabit,
  ensureParityWithConfigs,
} from '../Lib/LocalNotifications'
import PushNotification from 'react-native-push-notification'
import I18n from 'APP/I18n'
import { logger } from 'APP/Lib/Datadog'
import { RootState } from 'APP/Store/CreateStore'
import {
  BuiltInPrompt,
  CustomPrompt,
  DailyProgress,
  UserHabitWithStatus,
} from '@dialogue/services/dist/habits/habits/model'
import { Service as HabitsService } from '@dialogue/services/dist/habits/habits/service'
import { Service as CategoriesService } from '@dialogue/services/dist/habits/categories/service'
import type { Habit as HabitCMS } from '@dialogue/cms'

export const selectNotificationConfiguration = (
  state: RootState,
  habitId: string,
  type = Habits.Habits.Types.NotificationType.DO_HABIT
) => {
  const notificationsState = state.habits.notificationConfigurations?.[habitId]
  if (!notificationsState) return

  return {
    ...notificationsState,
    // @ts-expect-error Redux state is not typed
    data: (notificationsState.data || []).find((n) => n.type === type),
  }
}

export function* createHabits(): Generator<
  any,
  { Habits: HabitsService; Categories: CategoriesService },
  any
> {
  const accessToken: SagaReturnType<typeof selectAccessToken> = yield select(selectAccessToken)

  return {
    Habits: new Habits.Habits.Service(accessToken as string, Config.HABITS_SERVICE_URL),
    Categories: new Habits.Categories.Service(accessToken as string, Config.HABITS_SERVICE_URL),
  }
}

export const selectHabitById = (state: RootState, habitId: string) => {
  const { data } = state.habits.habits
  return data.find(({ identifier }) => identifier === habitId)
}

export const selectHasHabitActionInProgress = (state: RootState) => {
  return [
    state.habits.removeHabitPrompt.loading,
    state.habits.updateHabitPrompt.loading,
    state.habits.removeHabit.loading,
  ].some((loading) => loading === true)
}

export const selectAccessToken = (state: RootState) => state.login.accessToken

type ProgressStatsByHabit = {
  completedFirstTime: boolean
  weekly: {
    thirdTimeCompletedOn: string | undefined
    timesCompleted: number
  }
}

// Private methods start
function* getProgressStatsForHabits(
  habits: UserHabitWithStatus[]
): Generator<any, Record<string, ProgressStatsByHabit>, any> {
  const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

  let progressStatsByHabit: Record<string, ProgressStatsByHabit> = {}

  for (let i = 0; i < habits.length; i += 1) {
    // Get high level stats and progress this week
    const stats: SagaReturnType<typeof HabitsService.Habits.getStats> = yield call(
      HabitsService.Habits.getStats,
      {
        habitId: habits[i].identifier,
      }
    )

    const shouldFetchProgress = moment().isoWeekday() >= 3 // Wednesday (earliest day in week they could complete a habit 3 times)
    const progress: SagaReturnType<typeof HabitsService.Habits.getDailyProgress> =
      shouldFetchProgress
        ? yield call(HabitsService.Habits.getDailyProgress, {
            habitId: habits[i].identifier,
            fromDate: moment().isoWeekday(1).format(I18n.t('DateFormat')),
            toDate: moment().format(I18n.t('DateFormat')),
          })
        : []
    const [thirdTimeCompletedOn, timesCompleted] = progress.reduceRight(
      (
        acc: [string | undefined, number],
        { progress: { percentage }, day }: DailyProgress
      ): [string | undefined, number] => {
        let nextThirdTimeCompletedOn = acc[0]
        let nextTimesCompleted = acc[1]
        if (percentage === 100) nextTimesCompleted++
        if (nextTimesCompleted === 3 && !nextThirdTimeCompletedOn) nextThirdTimeCompletedOn = day
        return [nextThirdTimeCompletedOn, nextTimesCompleted]
      },
      [undefined, 0] as [string | undefined, number]
    )

    progressStatsByHabit[habits[i].identifier] = {
      completedFirstTime: stats.commits.done === 1,
      weekly: {
        thirdTimeCompletedOn,
        timesCompleted,
      },
    }
  }

  return progressStatsByHabit
}
// Private methods end

export function* getCategories(): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const categories: SagaReturnType<typeof HabitsService.Categories.getCategoryList> = yield call(
      HabitsService.Categories.getCategoryList
    )

    yield put(HabitActions.getCategoriesSuccess(categories))
  } catch (_e) {
    yield put(HabitActions.getCategoriesFailure())
  }
}

export function* getTopics({ id }: { id: string }): Generator {
  try {
    if (!id) throw Error('missing required parameters')
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const topics: SagaReturnType<typeof HabitsService.Categories.getCategoryTopicList> = yield call(
      HabitsService.Categories.getCategoryTopicList,
      {
        categoryId: id,
      }
    )

    yield put(HabitActions.getTopicsSuccess(topics))
  } catch (_e) {
    yield put(HabitActions.getTopicsFailure())
  }
}

export function* getSingleHabit({ id }: { id: string }): Generator {
  try {
    if (!id) throw Error('missing required parameters')

    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const habit: SagaReturnType<typeof HabitsService.Habits.getHabitDetails> = yield call(
      HabitsService.Habits.getHabitDetails,
      {
        habitId: id,
      }
    )

    yield put(HabitActions.getSingleHabitSuccess(habit))
  } catch (_e) {
    yield put(HabitActions.getSingleHabitFailure())
  }
}

export function* getSingleHabitStats({ id }: { id: string }): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const stats: SagaReturnType<typeof HabitsService.Habits.getStats> = yield call(
      HabitsService.Habits.getStats,
      {
        habitId: id,
      }
    )

    yield put(HabitActions.getSingleHabitStatsSuccess(id, stats))
  } catch (_e) {
    yield put(HabitActions.getSingleHabitStatsFailure(id))
  }
}

export function* getSingleHabitProgress({
  id,
  fromDate,
}: {
  id: string
  fromDate: string
}): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const progress: SagaReturnType<typeof HabitsService.Habits.getDailyProgress> = yield call(
      HabitsService.Habits.getDailyProgress,
      {
        habitId: id,
        fromDate,
        toDate: moment.utc().format(I18n.t('DateFormat')),
      }
    )

    yield put(HabitActions.getSingleHabitProgressSuccess(id, progress))
  } catch (_e) {
    yield put(HabitActions.getSingleHabitProgressFailure(id))
  }
}

export function* getHabitList({ date }: { date: string }): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    const habits: SagaReturnType<typeof HabitsService.Habits.getHabitList> = yield call(
      HabitsService.Habits.getHabitList,
      { date }
    )

    const progressStatsByHabit: SagaReturnType<typeof getProgressStatsForHabits> = yield call(
      getProgressStatsForHabits,
      habits
    )

    // 1. "To do" && newest -> oldest
    // 2. "Done" && newest -> oldest
    habits.sort(
      (a, b) =>
        a.status.percentage - b.status.percentage ||
        // @ts-expect-error FIXME: there is a typo in the interface, it should be started_at not start_at
        moment(b.started_at).unix() - moment(a.started_at).unix()
    )
    yield put(HabitActions.getHabitListSuccess(habits, progressStatsByHabit))
  } catch (_e) {
    yield put(HabitActions.getHabitListFailure())
  }
}

export function* getHabitContent({ id }: { id: string }): Generator {
  try {
    if (!id) throw Error('missing required parameters')

    const habitContent: SagaReturnType<typeof getHabit> = yield call(getHabit, id)

    if (!(habitContent as HabitCMS)?.id) {
      throw new Error('Habit not in CMS')
    }

    yield put(
      HabitActions.getHabitContentSuccess(id, {
        relatedContent: (habitContent as HabitCMS).related_content,
        expert: (habitContent as HabitCMS).expert,
      })
    )
  } catch (_e) {
    yield put(HabitActions.getHabitContentFailure(id))
  }
}

export function* startHabit({
  id,
  hasSupportingContent,
}: {
  id: string
  hasSupportingContent: boolean
}): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    // @ts-expect-error FIXME: according to the interface, prompt cannot be null
    yield call(HabitsService.Habits.startHabit, {
      habitId: id,
      userRequest: {
        prompt: null,
        start_date: moment().format(I18n.t('DateFormat')),
      },
    })
    yield put(
      HabitActions.startHabitSuccess(id, {
        showCupboard: hasSupportingContent,
        showPromptModal: true,
      })
    )
  } catch (_e) {
    yield put(HabitActions.startHabitFailure())
  }
}

export function* removeHabit({ id, date }: { id: string; date: string }): Generator {
  try {
    if (!id) throw Error('missing required parameters')

    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    yield call(HabitsService.Habits.stopHabit, {
      habitId: id,
    })

    call(removeLocalNotificationsForOneHabit, id)

    yield call(getHabitList, { date })

    yield put(HabitActions.removeHabitSuccess())
  } catch (e) {
    yield put(HabitActions.removeHabitFailure())
    logger.error('Failed to remove habit', { habitId: id }, e)
  }
}

export function* doneHabit({ id, date }: { id: string; date: string }): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    yield call(HabitsService.Habits.performHabit, {
      habitId: id,
      date,
    })
    yield call(getHabitList, { date })
    yield put(HabitActions.doneHabitSuccess())
  } catch (_e) {
    yield put(HabitActions.doneHabitFailure())
  }
}

export function* undoneHabit({ id, date }: { id: string; date: string }): Generator {
  try {
    if (!id) throw Error('missing required parameters')

    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    yield call(HabitsService.Habits.cancelHabit, {
      habitId: id,
      date,
    })

    yield call(getHabitList, { date })

    yield put(HabitActions.undoneHabitSuccess())
  } catch (_e) {
    yield put(HabitActions.undoneHabitFailure())
  }
}

export function* getDailyProgressList({
  fromDate,
  toDate,
}: {
  fromDate: string
  toDate: string
}): Generator {
  try {
    if (!fromDate || !toDate) {
      throw Error('missing required parameters')
    }
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    const dailyProgressList: SagaReturnType<typeof HabitsService.Habits.getDailyProgressList> =
      yield call(HabitsService.Habits.getDailyProgressList, {
        fromDate: moment(fromDate).format(I18n.t('DateFormat')),
        toDate: moment(toDate).format(I18n.t('DateFormat')),
      })
    yield put(HabitActions.getDailyProgressListSuccess(dailyProgressList))
  } catch (_e) {
    yield put(HabitActions.getDailyProgressListFailure())
  }
}

export function* removeHabitPrompt({ id, date }: { id: string; date: string }): Generator {
  try {
    if (!id) {
      throw Error('missing required parameters')
    }
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    yield call(HabitsService.Habits.removePrompt, {
      habitId: id,
    })
    if (date) {
      yield call(getHabitList, { date })
    }
    yield put(HabitActions.removeHabitPromptSuccess())
  } catch (_err) {
    yield put(HabitActions.removeHabitPromptFailure())
  }
}

export function* updateHabitPrompt({
  id,
  prompt,
  date,
}: {
  id: string
  prompt: CustomPrompt | BuiltInPrompt
  date: string
}): Generator {
  try {
    if (!id || !prompt) {
      throw Error('missing required parameters')
    }
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)
    if ((prompt as BuiltInPrompt).identifier) {
      yield call(HabitsService.Habits.updateBuiltInPrompt, {
        habitId: id,
        promptId: (prompt as BuiltInPrompt).identifier,
      })
    } else if ((prompt as CustomPrompt).custom_message) {
      yield call(HabitsService.Habits.updateCustomPrompt, {
        habitId: id,
        customMessage: (prompt as CustomPrompt).custom_message,
      })
    } else {
      throw Error('missing prompt required parameters')
    }
    if (date) {
      yield call(getHabitList, { date })
    }
    yield put(HabitActions.updateHabitPromptSuccess())
  } catch (_err) {
    yield put(HabitActions.updateHabitPromptFailure())
  }
}

export function* cancelAllHabitLocalNotifications(): Generator {
  try {
    yield call([PushNotification, PushNotification.cancelAllLocalNotifications])
  } catch (err) {
    logger.error('Failed to cancel habit local notifications', {}, err)
  }
}

export function* onHabitNotificationFeatureChange(): Generator {
  const isWellnessHabitsDisable: boolean = yield select((state: RootState) => {
    const eligibleServices = state.patient?.profile?.eligibleServices || {}
    return !('wellness_habits' in eligibleServices)
  })

  if (isWellnessHabitsDisable) {
    yield call(cancelAllHabitLocalNotifications)
  }
}

export function* getAllNotificationConfigurations(): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const notifications: SagaReturnType<typeof HabitsService.Habits.getAllNotifications> =
      yield call(HabitsService.Habits.getAllNotifications, { enabled: true })

    yield put(HabitActions.getAllNotificationConfigurationsSuccess(notifications))
  } catch (err) {
    yield put(HabitActions.getAllNotificationConfigurationsFailure())
    logger.error('Failed to get all notification configurations', {}, err)
  }
}

export function* getNotificationConfigurations({ habitId }: { habitId: string }): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const notifications: SagaReturnType<typeof HabitsService.Habits.getNotifications> = yield call(
      HabitsService.Habits.getNotifications,
      { habitId }
    )

    yield put(HabitActions.getNotificationConfigurationsSuccess(habitId, notifications))
  } catch (err) {
    yield put(HabitActions.getNotificationConfigurationsFailure(habitId))
    logger.error('Failed to get notification configurations', {}, err)
  }
}

export function* updateNotificationConfiguration({
  habitId,
  notificationType,
  values,
}: {
  habitId: string
  notificationType: string
  values: { enabled: boolean; weekDays: number[]; reminderTime: string }
}): Generator {
  try {
    const HabitsService: SagaReturnType<typeof createHabits> = yield call(createHabits)

    const notification: SagaReturnType<typeof HabitsService.Habits.updateNotification> = yield call(
      // @ts-expect-error FIXME: according to the interface, reminderTime cannot be null
      HabitsService.Habits.updateNotification,
      {
        habitId,
        notificationType,
        enabled: values.enabled,
        weekDays: values.enabled ? values.weekDays : null,
        reminderTime: values.enabled ? values.reminderTime : null,
      }
    )

    yield call(updateLocalNotificationsForOneHabit, notification)
    yield call(getAllNotificationConfigurations)

    yield put(HabitActions.updateNotificationConfigurationSuccess(habitId, notification))
  } catch (err) {
    yield put(HabitActions.updateNotificationConfigurationFailure())
    logger.error('Failed to update notification configuration', {}, err)
  }
}

export function* watchForHabitNotificationSafetyCheck(action: () => Generator): Generator {
  while (true) {
    // Is explicitly triggered on startup; not aiming to run syncs on live updates.
    // Trigger action WHEN:
    // 1. Member is already logged in and data is successfully fetched
    // 2. Member has just logged in and data is successfully fetched
    yield race({
      reAuth: all([
        take(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS),
        take(featuresActions.setFeatures),
      ]),
      login: all([take(LoginTypes.LOGIN_SUCCESS), take(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS)]),
    })

    yield call(action)
  }
}

export function* ensureNotificationParity(): Generator {
  const isWellnessHabitsEnabled: boolean = yield select((state: RootState) => {
    const eligibleServices = state.patient?.profile?.eligibleServices || {}
    return 'wellness_habits' in eligibleServices
  })

  if (isWellnessHabitsEnabled) {
    try {
      yield call(getAllNotificationConfigurations)

      const notificationConfigs = yield select(
        (state: RootState) => state.habits.allNotificationConfigurations.notifications
      )

      yield call(ensureParityWithConfigs, notificationConfigs)
    } catch (err) {
      logger.error('Failed to ensure habit notification parity', {}, err)
    }
  }
}
